import { toDegrees, clamp, roundFloat } from './math-util.js';
export function RotatePinnedVector(origin, v, q) {
    var vRotated = new Vector3(q.rotateVector(v.toArray()));
    return vRotated.add(origin);
}
export function RotateVector(base, tip, q) {
    var direction = tip.minus(base);
    var vRotated = new Vector3(q.rotateVector(direction.toArray()));
    return vRotated.add(base);
}
export function VString(a) {
    var format = function (value) { return value.toFixed(2).padStart(5); };
    if (a.w !== undefined && a.x !== undefined && a.y !== undefined && a.z !== undefined) {
        return format(a.w) + " " + format(a.x) + " " + format(a.y) + " " + format(a.z) + " ";
    }
    if (a.x !== undefined && a.y !== undefined && a.z !== undefined) {
        return format(a.x) + " " + format(a.y) + " " + format(a.z) + " ";
    }
    return "ERROR: vector format not handled type:'" + typeof a + "' " + a;
}
var Vector3 = (function () {
    function Vector3(x, y, z) {
        if (x === undefined) {
            this.x = 0;
            this.y = 0;
            this.z = 0;
        }
        else if (typeof x == 'number') {
            this.x = x;
            this.y = y;
            this.z = z;
        }
        else if (Array.isArray(x) === true) {
            this.x = x[0];
            this.y = x[1];
            this.z = x[2];
        }
        else if (x instanceof Vector3) {
            this.x = x.x;
            this.y = x.y;
            this.z = x.z;
        }
        else {
            console.log('ERROR Vector3 unhandled constructor type', typeof x, x);
        }
    }
    Vector3.prototype.set = function (x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
        return this;
    };
    Vector3.prototype.update = function (other) {
        this.x = other.x;
        this.y = other.y;
        this.z = other.z;
        return this;
    };
    Vector3.prototype.floored = function () {
        return new Vector3(Math.floor(this.x), Math.floor(this.y), Math.floor(this.z));
    };
    Vector3.prototype.floor = function () {
        this.x = Math.floor(this.x);
        this.y = Math.floor(this.y);
        this.z = Math.floor(this.z);
        return this;
    };
    Vector3.prototype.offset = function (dx, dy, dz) {
        return new Vector3(this.x + dx, this.y + dy, this.z + dz);
    };
    Vector3.prototype.translate = function (dx, dy, dz) {
        this.x += dx;
        this.y += dy;
        this.z += dz;
        return this;
    };
    Vector3.prototype.add = function (other) {
        this.x += other.x;
        this.y += other.y;
        this.z += other.z;
        return this;
    };
    Vector3.prototype.subtract = function (other) {
        return new Vector3(this.x - other.x, this.y - other.y, this.z - other.z);
    };
    Vector3.prototype.plus = function (other) {
        return this.offset(other.x, other.y, other.z);
    };
    Vector3.prototype.minus = function (other) {
        return this.offset(-other.x, -other.y, -other.z);
    };
    Vector3.prototype.scaled = function (scalar) {
        return new Vector3(this.x * scalar, this.y * scalar, this.z * scalar);
    };
    Vector3.prototype.abs = function () {
        return new Vector3(Math.abs(this.x), Math.abs(this.y), Math.abs(this.z));
    };
    Vector3.prototype.volume = function () {
        return this.x * this.y * this.z;
    };
    Vector3.prototype.distanceTo = function (other) {
        var dx = other.x - this.x;
        var dy = other.y - this.y;
        var dz = other.z - this.z;
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    };
    Vector3.prototype.distanceSquared = function (other) {
        var dx = other.x - this.x;
        var dy = other.y - this.y;
        var dz = other.z - this.z;
        return dx * dx + dy * dy + dz * dz;
    };
    Vector3.prototype.equals = function (other) {
        return this.x === other.x && this.y === other.y && this.z === other.z;
    };
    Vector3.prototype.toString = function () {
        return "x:" + this.x.toFixed(2) + " y:" + this.y.toFixed(2) + " z:" + this.z.toFixed(2);
    };
    Vector3.prototype.clone = function () {
        return this.offset(0, 0, 0);
    };
    Vector3.prototype.min = function (other) {
        return new Vector3(Math.min(this.x, other.x), Math.min(this.y, other.y), Math.min(this.z, other.z));
    };
    Vector3.prototype.max = function (other) {
        return new Vector3(Math.max(this.x, other.x), Math.max(this.y, other.y), Math.max(this.z, other.z));
    };
    Vector3.prototype.norm = function () {
        return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
    };
    Vector3.prototype.dot = function (other) {
        return this.x * other.x + this.y * other.y + this.z * other.z;
    };
    Vector3.prototype.cross = function (other) {
        return new Vector3(this.y * other.z - this.z * other.y, this.z * other.x - this.x * other.z, this.x * other.y - this.y * other.x);
    };
    Vector3.prototype.unit = function () {
        var norm = this.norm();
        if (norm === 0) {
            return this.clone();
        }
        return this.scaled(1 / norm);
    };
    Vector3.prototype.normalize = function () {
        var norm = this.norm();
        if (norm !== 0) {
            this.x /= norm;
            this.y /= norm;
            this.z /= norm;
        }
        return this;
    };
    Vector3.prototype.scale = function (scalar) {
        this.x *= scalar;
        this.y *= scalar;
        this.z *= scalar;
        return this;
    };
    Vector3.prototype.round = function (precision) {
        return new Vector3(roundFloat(this.x, precision), roundFloat(this.y, precision), roundFloat(this.z, precision));
    };
    Vector3.prototype.xyDistanceTo = function (other) {
        var dx = other.x - this.x;
        var dy = other.y - this.y;
        return Math.sqrt(dx * dx + dy * dy);
    };
    Vector3.prototype.xzDistanceTo = function (other) {
        var dx = other.x - this.x;
        var dz = other.z - this.z;
        return Math.sqrt(dx * dx + dz * dz);
    };
    Vector3.prototype.yzDistanceTo = function (other) {
        var dy = other.y - this.y;
        var dz = other.z - this.z;
        return Math.sqrt(dy * dy + dz * dz);
    };
    Vector3.prototype.innerProduct = function (other) {
        return this.x * other.x + this.y * other.y + this.z * other.z;
    };
    Vector3.prototype.manhattanDistanceTo = function (other) {
        return Math.abs(other.x - this.x) + Math.abs(other.y - this.y) + Math.abs(other.z - this.z);
    };
    Vector3.prototype.toArray = function () {
        return [this.x, this.y, this.z];
    };
    Vector3.prototype.scalarProjection = function (v) {
        return this.dot(v) / this.norm();
    };
    return Vector3;
}());
export { Vector3 };
export function MultiplyVectors(a, b) {
    return new Vector3(a.x * b.x, a.y * b.y, a.z * b.z);
}
export function AngleBetweenVectors(v1, v2) {
    var arg = v1.dot(v2) / (v1.norm() * v2.norm());
    return Math.acos(clamp(arg, -1, 1));
}
export function midpoint(v1, v2) {
    return v2.minus(v1).scaled(0.5)
        .plus(v1);
}
export function angleInPlane(v1, v2, vNormal) {
    if (roundFloat(Math.abs(v2.dot(vNormal)), 6) === 1) {
        return 0;
    }
    var projection = v2.minus(vNormal.scaled(v2.dot(vNormal)));
    var dot = projection.unit().dot(v1);
    if (dot < -0.9999) {
        return 0;
    }
    var sign = Math.sign(v1.cross(v2).dot(vNormal));
    var radians = sign * AngleBetweenVectors(v1, projection);
    return Math.round(toDegrees(radians));
}
Vector3['X'] = new Vector3(1, 0, 0);
Vector3['Y'] = new Vector3(0, 1, 0);
Vector3['Z'] = new Vector3(0, 0, 1);
//# sourceMappingURL=vector-utils.js.map